import { sortArrayAlphabetically, getPriorityWeight } from "@datahooks/metadata/DataUtils";

/**
 * @typedef {Object} FilterField
 * @property {string} key - Field key (e.g. 'status', 'priority')
 * @property {string} label - Human-readable label
 * @property {'text' | 'dropdown' | 'date'} type - Type of input
 * @property {string[] | Array<{value: string, label: string}>} [options] - For dropdown: array of strings OR array of {value, label} objects
 * @property {function} [transform] - Optional function to transform string values before displaying (ignored if options are objects)
 * @property {boolean} [advanced] - If true, this filter will be in the advanced/collapsible section
 */

/**
 * Extracts a list of unique, alphabetically sorted string values
 * from a specified key in an array of objects.
 *
 * This is useful for generating dropdown options for filters.
 *
 * @template T
 * @param {T[]} items - The array of objects to extract values from
 * @param {keyof T} key - The key to extract values by (e.g. 'status', 'area')
 * @returns {string[]} Alphabetically sorted list of unique values
 *
 * @example
 * getOptions([{ status: 'Passed' }, { status: 'Failed' }], 'status'); gives ['Failed', 'Passed']
 */
export const getOptions = (items, key) => {
  if (!Array.isArray(items)) return [];

  const rawValues = items.map(item => item?.[key]).filter(Boolean);
  const unique = [...new Set(rawValues)];

  return sortArrayAlphabetically(unique);
};

/**
 * Builds dropdown options from an array of objects using string keys for value and label.
 * Supports pinning a specific label (e.g. 'Other') to the end of the sorted list.
 *
 * @template T
 * @param {T[]} items - The array of source objects
 * @param {keyof T} valueKey - The key to use for the option value
 * @param {keyof T} [labelKey=valueKey] - The key to use for the option label
 * @param {string|null} [pinToEnd=null] - Optional label to pin to the bottom of the sorted list
 * @returns {{ value: string, label: string }[]} An array of dropdown option objects
 */
export const buildSelectOptions = (items, valueKey, labelKey = valueKey, pinToEnd = null) => {
  if (!Array.isArray(items)) return [];

  const seen = new Set();
  const opts = [];

  for (const item of items) {
    const value = item?.[valueKey];
    const label = item?.[labelKey];

    if (value != null && label != null) {
      // SonarQube S6551: We've explicitly guarded against object stringification when assigning value and label below
      // so any SonarQube S6551 issues here are false positives.
      const stringValue = typeof value !== 'object' ? String(value) : '';
      const stringLabel = typeof label !== 'object' ? String(label) : '';

      if (!seen.has(stringValue)) {
        seen.add(stringValue);
        opts.push({ value: stringValue, label: stringLabel });
      }
    }
  }

  opts.sort((a, b) => {
    if (pinToEnd) {
      if (a.label === pinToEnd) return 1;
      if (b.label === pinToEnd) return -1;
    }
    return a.label.localeCompare(b.label);
  });

  return opts;
};

/**
 * Extracts a list of unique priority values and sorts them
 * using predefined priority weights (e.g. P1 > P2 > LOW).
 *
 * @template T
 * @param {T[]} items - The array of objects to extract priorities from
 * @param {keyof T} [key='priority'] - The key to extract (defaults to 'priority')
 * @returns {string[]} List of priorities sorted by descending importance
 *
 * @example
 * getPriorityOptions([{ priority: 'P2' }, { priority: 'P1' }]);   gives ['P1', 'P2']
 */
export const getPriorityOptions = (items, key = 'priority') => {
  if (!Array.isArray(items)) return [];

  const rawValues = items.map(item => item?.[key]).filter(Boolean);
  const unique = [...new Set(rawValues)];

  return unique.sort((a, b) => getPriorityWeight(b) - getPriorityWeight(a));
};